/**
 * Terminal statuses, as an `as const` tuple so the model can spread it into
 * `.whereNotIn('status', TERMINAL)` and the predicate below can derive from it.
 */
export const WORKFLOW_TERMINAL_STATUSES = ['completed', 'failed'];
export const isWorkflowInProgress = (status) => !WORKFLOW_TERMINAL_STATUSES.includes(status);
/**
 * How a run starts. `manual` is a direct startRun call; `schedule` is expanded
 * into Graphile jobs by the daily generator, the same way scheduled deliveries
 * are.
 */
/**
 * What a workflow can listen for.
 *
 * Batch semantics: one of these fires once per completed import or sync,
 * carrying that batch's row count. Deliberately not the per-row
 * `feedback.created` name other products use — firing per row would create a
 * run per feedback item, each materialising DB rows and a queue job per step,
 * and any AI node would then bill once per row. `feedback.created` stays free
 * for that later, guarded, per-row slice.
 */
export const WORKFLOW_EVENT_TYPES = [
    'feedback.imported',
    'connector.synced',
    /**
     * One per table a connector sync wrote rows to, once per sync, never per
     * row. For connectors that sync incrementally these are the changed
     * records; a full-refresh connector reports every row it re-landed.
     */
    'connector.records_changed',
];
/** Variables every event-triggered run starts with, whichever event fired it. */
export const WORKFLOW_EVENT_VARIABLE_NAMES = [
    'eventType',
    'eventSourceUuid',
    'eventSourceName',
    'eventExploreName',
    'eventRowsWritten',
    'eventTableName',
];
export const MAX_WORKFLOW_FORM_FIELDS = 30;
export const MAX_WORKFLOW_FORM_VALUE_LENGTH = 5000;
const FORM_FIELD_NAME = /^[A-Za-z_]\w{0,63}$/;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const getFormFieldProblems = (fields) => {
    const problems = [];
    if (fields.length === 0)
        problems.push('A form needs at least one field');
    if (fields.length > MAX_WORKFLOW_FORM_FIELDS) {
        problems.push(`A form can have at most ${MAX_WORKFLOW_FORM_FIELDS} fields`);
    }
    const seen = new Set();
    fields.forEach((field) => {
        if (!FORM_FIELD_NAME.test(field.name)) {
            problems.push(`Form field "${field.name}" must be letters, digits or underscores, not starting with a digit`);
        }
        if (seen.has(field.name)) {
            problems.push(`Form field "${field.name}" is listed twice`);
        }
        seen.add(field.name);
        if (field.label.trim().length === 0 || field.label.length > 200) {
            problems.push(`Form field "${field.name}" needs a label of up to 200 characters`);
        }
    });
    return problems;
};
/** Checks a public submission against the declared fields; unknown keys are refused. */
export const validateFormSubmission = (fields, values) => {
    const problems = [];
    const declared = new Map(fields.map((field) => [field.name, field]));
    Object.keys(values).forEach((key) => {
        if (!declared.has(key))
            problems.push(`Unknown form field "${key}"`);
    });
    const variables = {};
    fields.forEach((field) => {
        const raw = values[field.name];
        const value = typeof raw === 'string' ? raw.trim() : '';
        if (raw !== undefined && typeof raw !== 'string') {
            problems.push(`"${field.label}" must be text`);
        }
        else if (value.length === 0) {
            if (field.required)
                problems.push(`"${field.label}" is required`);
        }
        else if (value.length > MAX_WORKFLOW_FORM_VALUE_LENGTH) {
            problems.push(`"${field.label}" is longer than ${MAX_WORKFLOW_FORM_VALUE_LENGTH} characters`);
        }
        else if (field.type === 'email' && !EMAIL_SHAPE.test(value)) {
            problems.push(`"${field.label}" is not an email address`);
        }
        variables[field.name] = value;
    });
    return { variables, problems };
};
/** Same shape interpolation matches, so every field is referenceable. */
export const WORKFLOW_AI_OUTPUT_FIELD_NAME = /^[A-Za-z_]\w*$/;
/** Always set by askAiAgent, so a field cannot shadow them. */
export const WORKFLOW_AI_RESERVED_OUTPUTS = [
    'aiAnswer',
    'aiAgentName',
    'aiTokensUsed',
];
/** Every reason a node's output fields could not run, empty when fine. */
export const getAiOutputFieldProblems = (fields) => {
    const problems = [];
    const seen = new Set();
    fields.forEach((field) => {
        if (!WORKFLOW_AI_OUTPUT_FIELD_NAME.test(field.name)) {
            problems.push(`Output field "${field.name}" must be letters, digits or underscores, not starting with a digit`);
        }
        if (WORKFLOW_AI_RESERVED_OUTPUTS.includes(field.name)) {
            problems.push(`Output field "${field.name}" is reserved`);
        }
        if (seen.has(field.name)) {
            problems.push(`Output field "${field.name}" is listed twice`);
        }
        seen.add(field.name);
        if (field.type === 'select') {
            const options = field.options.map((option) => option.trim());
            if (options.filter((option) => option.length > 0).length < 2) {
                problems.push(`Output field "${field.name}" needs at least two options`);
            }
            if (new Set(options).size !== options.length) {
                problems.push(`Output field "${field.name}" has duplicate options`);
            }
        }
    });
    return problems;
};
/**
 * Upper bound on a delay. A wait is held open by a chain of rechecks, so an
 * unbounded one would keep a run alive indefinitely; a day is well past any
 * "let the warehouse settle" pause and still finite.
 */
export const MAX_WAIT_SECONDS = 24 * 60 * 60;
/** Bound on how long an httpRequest node may wait on a response. */
export const HTTP_REQUEST_TIMEOUT_MS = 30000;
/** Bound on a response body an httpRequest node will read into a variable. */
export const MAX_HTTP_RESPONSE_BYTES = 1000000;
export const isConditionAction = (action) => action.kind === 'condition';
/**
 * Every rule the engine relies on, checked in one place so the canvas can show
 * the same errors before saving rather than the server rejecting a drawing.
 *
 * ponytail: a plain node may have at most one outgoing edge. Fan-out would mean
 * running branches concurrently, which the sequential engine cannot do — allow
 * it once the engine can join.
 */
export const validateWorkflowGraph = (graph) => {
    const problems = [];
    const { nodes, edges } = graph;
    if (nodes.length === 0) {
        return [
            { message: 'A workflow needs at least one node', nodeId: null },
        ];
    }
    const byId = new Map();
    nodes.forEach((node) => {
        if (byId.has(node.nodeId)) {
            problems.push({
                message: `Duplicate node id "${node.nodeId}"`,
                nodeId: node.nodeId,
            });
        }
        byId.set(node.nodeId, node);
        if (node.action.kind === 'askAiAgent') {
            getAiOutputFieldProblems(node.action.outputFields ?? []).forEach((message) => problems.push({ message, nodeId: node.nodeId }));
        }
        // The run re-checks after interpolation; this catches the literal case
        // early, so a step that can never run is not saved.
        if (node.action.kind === 'connectorRequest' &&
            (/^[a-z][a-z0-9+.-]*:/i.test(node.action.path.trim()) ||
                node.action.path.trim().startsWith('//'))) {
            problems.push({
                message: 'Use a path relative to the app API, not a full URL',
                nodeId: node.nodeId,
            });
        }
    });
    edges.forEach((edge) => {
        if (!byId.has(edge.fromNodeId)) {
            problems.push({
                message: `Edge starts at unknown node "${edge.fromNodeId}"`,
                nodeId: null,
            });
        }
        if (!byId.has(edge.toNodeId)) {
            problems.push({
                message: `Edge ends at unknown node "${edge.toNodeId}"`,
                nodeId: edge.fromNodeId,
            });
        }
        if (edge.fromNodeId === edge.toNodeId) {
            problems.push({
                message: 'A node cannot connect to itself',
                nodeId: edge.fromNodeId,
            });
        }
    });
    const withIncoming = new Set(edges.map((edge) => edge.toNodeId));
    const entries = nodes.filter((node) => !withIncoming.has(node.nodeId));
    if (entries.length === 0) {
        problems.push({
            message: 'Every node has an incoming connection, so nothing starts',
            nodeId: null,
        });
    }
    if (entries.length > 1) {
        problems.push({
            message: `A workflow needs one starting node, found ${entries.length}`,
            nodeId: null,
        });
    }
    // Per-node outgoing rules: a condition needs both branches labelled and
    // distinct; anything else takes one unlabelled edge.
    nodes.forEach((node) => {
        const outgoing = edges.filter((edge) => edge.fromNodeId === node.nodeId);
        if (isConditionAction(node.action)) {
            const labelled = outgoing.filter((edge) => edge.when !== null);
            if (labelled.length !== outgoing.length) {
                problems.push({
                    message: `"${node.label}" is a condition, so each connection must be the true or false branch`,
                    nodeId: node.nodeId,
                });
            }
            ['true', 'false'].forEach((branch) => {
                if (outgoing.filter((edge) => edge.when === branch).length > 1) {
                    problems.push({
                        message: `"${node.label}" has more than one ${branch} branch`,
                        nodeId: node.nodeId,
                    });
                }
            });
        }
        else {
            if (outgoing.some((edge) => edge.when !== null)) {
                problems.push({
                    message: `Only a condition can have true/false branches, and "${node.label}" is not one`,
                    nodeId: node.nodeId,
                });
            }
            if (outgoing.length > 1) {
                problems.push({
                    message: `"${node.label}" connects to more than one node; only a condition can split the path`,
                    nodeId: node.nodeId,
                });
            }
        }
    });
    // Cycles and reachability, from the single entry when there is one.
    const entry = entries.length === 1 ? entries[0] : undefined;
    if (entry) {
        const visiting = new Set();
        const settled = new Set();
        const walk = (nodeId) => {
            if (settled.has(nodeId))
                return;
            if (visiting.has(nodeId)) {
                problems.push({
                    message: `The connections loop back to "${byId.get(nodeId)?.label ?? nodeId}", so the workflow would never finish`,
                    nodeId,
                });
                return;
            }
            visiting.add(nodeId);
            edges
                .filter((edge) => edge.fromNodeId === nodeId)
                .forEach((edge) => {
                if (byId.has(edge.toNodeId))
                    walk(edge.toNodeId);
            });
            visiting.delete(nodeId);
            settled.add(nodeId);
        };
        walk(entry.nodeId);
        nodes
            .filter((node) => !settled.has(node.nodeId))
            .forEach((node) => {
            problems.push({
                message: `"${node.label}" is never reached from the starting node`,
                nodeId: node.nodeId,
            });
        });
    }
    return problems;
};
export const MAX_WORKFLOW_VERSION_DESCRIPTION_LENGTH = 500;
export const MAX_WORKFLOW_SOP_LENGTH = 20000;
/** Node kinds that suspend and resume rather than completing in one attempt. */
export const isSuspendingAction = (action) => (action.kind === 'syncConnector' && action.waitForCompletion) ||
    action.kind === 'refreshProject' ||
    action.kind === 'waitForDuration' ||
    action.kind === 'requestApproval';
//# sourceMappingURL=workflow.js.map